/**
 * Hedef kaydından canlı hedef + kullanıcıya sunulacak seçenek listesi.
 *
 * Bir seçenek KULLANILAMIYORSA gizlenmez, sebebiyle gösterilir ("bu
 * tarayıcıda yok", "bu sunucuda yapılandırılmamış"). Kullanıcı neden
 * göremediğini sormak zorunda kalmasın.
 */

import { M } from "../../dil";
import type { BulutIstemcileri, BulutBagimliliklari } from "../oauth";
import type { BulutHedefi, HedefKaydi, HedefTuru } from "../tipler";
import { dropboxBaglan, dropboxHedefi } from "./dropbox";
import { googleBaglan, googleDriveHedefi } from "./googleDrive";
import { onedriveBaglan, onedriveHedefi } from "./onedrive";
import { parafonSunucuBaglan, parafonSunucuHedefi } from "./parafonSunucu";
import type { KasaIstegi, SunucuSecimi } from "./parafonSunucu";
import { KASA_SAKLAMA_AY, KASA_SUNUCU_PAROLA_EN_AZ } from "@parafon/contracts/central-api";
import { yerelKlasorEngeli, yerelKlasorHedefi, yerelKlasorSec } from "./yerelKlasor";

export interface HedefSecenegi {
  readonly tur: HedefTuru;
  /** null = kullanılabilir; aksi hâlde sebebi. */
  readonly engel: string | null;
  /** Kullanıcıya kısa not (sınırı). */
  readonly not: string;
}

const YAPILANDIRILMAMIS = M.yedek.hedef.yapilandirilmamis;
const MERKEZ_YOK = M.yedek.hedef.merkezYok;

/** Sıra (19.10.2026): hesap gerektirmeyen Parafon sunucusu önce, eşitlenen klasör sonda. */
export function hedefSecenekleri(istemci: BulutIstemcileri): readonly HedefSecenegi[] {
  return [
    {
      tur: "parafon-sunucu",
      engel: istemci.merkez === null || istemci.merkez === undefined ? MERKEZ_YOK : null,
      not: M.yedek.hedef.sunucuNot(KASA_SUNUCU_PAROLA_EN_AZ, KASA_SAKLAMA_AY),
    },
    {
      tur: "dropbox",
      engel: istemci.dropbox === null ? YAPILANDIRILMAMIS : null,
      not: M.yedek.hedef.dropboxNot,
    },
    {
      tur: "google-drive",
      engel: istemci.google === null ? YAPILANDIRILMAMIS : null,
      not: M.yedek.hedef.googleNot,
    },
    {
      tur: "onedrive",
      engel: istemci.onedrive === null ? YAPILANDIRILMAMIS : null,
      not: M.yedek.hedef.onedriveNot,
    },
    {
      tur: "yerel-klasor",
      engel: yerelKlasorEngeli(),
      not: M.yedek.hedef.klasorNot,
    },
  ];
}

function merkezGerek(v: KasaIstegi | null | undefined): KasaIstegi {
  if (v === null || v === undefined) throw new Error(MERKEZ_YOK);
  return v;
}

function istemciGerek(v: string | null, ad: string): string {
  if (v === null) throw new Error(M.yedek.hedef.adYapilandirilmamis(ad, YAPILANDIRILMAMIS));
  return v;
}

export function hedefAc(kayit: HedefKaydi, istemci: BulutIstemcileri, d: BulutBagimliliklari): BulutHedefi {
  switch (kayit.tur) {
    case "yerel-klasor":
      return yerelKlasorHedefi(kayit);
    case "dropbox":
      return dropboxHedefi(kayit, istemciGerek(istemci.dropbox, "Dropbox"), d);
    case "google-drive":
      return googleDriveHedefi(kayit, istemciGerek(istemci.google, "Google Drive"), d);
    case "onedrive":
      return onedriveHedefi(kayit, istemciGerek(istemci.onedrive, "OneDrive"), d);
    case "parafon-sunucu":
      return parafonSunucuHedefi(kayit, merkezGerek(istemci.merkez));
  }
}

/**
 * Kullanıcı hareketinin içinden: yeni bir hedefe bağlanır. Parafon sunucusu
 * için `sunucu` zorunlu: yeni kod mu, var olan kod mu.
 */
export function hedefeBaglan(
  tur: HedefTuru,
  istemci: BulutIstemcileri,
  d: BulutBagimliliklari,
  sunucu?: SunucuSecimi,
): Promise<HedefKaydi> {
  switch (tur) {
    case "yerel-klasor":
      return yerelKlasorSec();
    case "dropbox":
      return dropboxBaglan(istemciGerek(istemci.dropbox, "Dropbox"), d);
    case "google-drive":
      return googleBaglan(istemciGerek(istemci.google, "Google Drive"), d);
    case "onedrive":
      return onedriveBaglan(istemciGerek(istemci.onedrive, "OneDrive"), d);
    case "parafon-sunucu":
      if (sunucu === undefined) return Promise.reject(new Error(M.yedek.hedef.sunucuSecin));
      return parafonSunucuBaglan(merkezGerek(istemci.merkez), sunucu);
  }
}
